import React, { useContext, useEffect, useMemo } from 'react';
import { BrowserRouter, useInRouterContext } from 'react-router-dom';
import {
  Provider as ReduxProvider,
  ReactReduxContext,
  useDispatch,
} from 'react-redux';
import { ApiProvider } from './context/ApiContext';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import defaultStore from './redux/store';
import App from './App';
import { setAppDetails } from './redux/appDetails/reducer';
import './styles/index.css';

function ThemedShell({ children }) {
  const { cssVars, darkMode, mode } = useTheme();

  return (
    <div
      className={`ca-root ${darkMode ? 'ca-root--dark' : 'ca-root--light'}`}
      data-ca-theme={mode}
      style={cssVars}
    >
      {children}
    </div>
  );
}

function OptionalRouter({ basePath, children }) {
  const hasParentRouter = useInRouterContext();

  if (hasParentRouter) return children;
  return <BrowserRouter basename={basePath}>{children}</BrowserRouter>;
}

function OptionalReduxProvider({ store, children }) {
  const parentRedux = useContext(ReactReduxContext);

  // Reuse the parent's provider by default. An explicit store remains useful
  // for isolation, while the package store keeps standalone previews working.
  if (!store && parentRedux?.store) return children;
  return <ReduxProvider store={store || defaultStore}>{children}</ReduxProvider>;
}

function AppDetailsSync({ details, children }) {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(setAppDetails(details));
  }, [dispatch, details]);

  return children;
}

/**
 * Root package component — mount inside the parent app.
 *
 * Theme & dark mode are parent-controlled:
 *   <ComplaintApp theme={brandTheme} darkMode={parentIsDark} />
 */
const ComplaintApp = ({
  apiUrl,
  authToken,
  store,
  basePath = '/',
  theme,
  darkMode = false,
  title,
  description,
  rolelist,
  systems,
  systemsEndpoint,
  useDemoData = true,
  createLabel,
  onCreateForm,
  onConfigure,
  onOpen,
  onTitleClick,
  onDescriptionClick,
  onSearch,
  onPublish,
  onSaveDraft,
  showSaveButton,
  showNextButton,
  onStepChange,
  children,
}) => {
  const pageProps = {
    title,
    description,
    systems,
    systemsEndpoint,
    useDemoData,
    createLabel,
    onCreateForm,
    onConfigure,
    onOpen,
    rolelist,
    onTitleClick,
    onDescriptionClick,
    onSearch,
    onSaveDraft,
    showSaveButton,
    showNextButton,
  };

  const appDetails = useMemo(
    () => ({
      apiUrl,
      authToken,
      basePath,
      theme,
      darkMode,
      title,
      description,
      rolelist,
      systems,
      systemsEndpoint,
      useDemoData,
      createLabel,
      onCreateForm,
      onConfigure,
      onOpen,
      onTitleClick,
      onDescriptionClick,
      onSearch,
      onPublish,
      onSaveDraft,
      showSaveButton,
      showNextButton,
      onStepChange,
    }),
    [
      apiUrl,
      authToken,
      basePath,
      theme,
      darkMode,
      title,
      description,
      rolelist,
      systems,
      systemsEndpoint,
      useDemoData,
      createLabel,
      onCreateForm,
      onConfigure,
      onOpen,
      onTitleClick,
      onDescriptionClick,
      onSearch,
      onPublish,
      onSaveDraft,
      showSaveButton,
      showNextButton,
      onStepChange,
    ],
  );

  return (
    <ThemeProvider theme={theme} darkMode={darkMode}>
      <OptionalReduxProvider store={store}>
        <AppDetailsSync details={appDetails}>
          <ThemedShell>
            <ApiProvider apiUrl={apiUrl} authToken={authToken}>
              <OptionalRouter basePath={basePath}>
                <App
                  pageProps={pageProps}
                  onPublish={onPublish}
                  onStepChange={onStepChange}
                >
                  {children}
                </App>
              </OptionalRouter>
            </ApiProvider>
          </ThemedShell>
        </AppDetailsSync>
      </OptionalReduxProvider>
    </ThemeProvider>
  );
};

export default ComplaintApp;
